import type { FC } from 'react';
import React, { memo, useState, useEffect, useRef, Fragment } from 'react';
import { css, styled } from '@compiled/react';
import { FormattedMessage, defineMessages } from 'react-intl';
import { graphql } from 'react-relay';

import { token } from '@atlaskit/tokens';
import EditIcon from '@atlaskit/icon/core/edit';
import Button from '@atlaskit/button';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { HOME_RIGHT_PANEL_WELCOME_MESSAGE_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceSuccess } from '@confluence/experience-tracker/entry-points/component.ts';
import { Link } from '@confluence/route-manager/entry-points/Link.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import { CollapsibleSection } from '../../CollapsibleSection.tsx';
import { LoadingDone } from '../LoadingListener/LoadingDone.tsx';

import { transformData } from './transformWelcomeMessages.ts';
import type { WelcomeMessagesValue } from './transformWelcomeMessages.ts';
import { WelcomeMessageError } from './WelcomeMessageError.tsx';
import type {
	WelcomeMessagesQuery$data,
	WelcomeMessagesQuery$variables,
} from './__generated__/WelcomeMessagesQuery.graphql.ts';

const WelcomeMessagesQuery = graphql`
	query WelcomeMessagesQuery {
		spaViewContext {
			welcomeMessageEditUrl
			welcomeMessageHtml
			resourceKeys
			resourceContexts
			showWelcomeMessageEditHint
			showEditButton
		}
	}
`;

const welcomeMessageStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	iframe: {
		width: '100%',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'*': {
		font: token('font.body.large'),
	},
	display: 'flex',
	position: 'relative',
});

const announcementsContainerStyles = css({
	width: '100%',
	contain: 'content',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const EditLinkCompiled = styled(Link)({
	float: 'right',
	height: '24px',
});

const customizeLinkStyles = css({
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	marginTop: '15px',
});

const headerContainerStyles = css({
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
	flex: '1',
	color: token('color.text'),
	fontWeight: token('font.weight.medium'),
});

const i18n = defineMessages({
	customize: {
		id: 'home.welcomeMessage.customize',
		defaultMessage: 'Customize',
		description: "The word 'customize' to edit a welcome message",
	},
	moreInfo: {
		id: 'home.welcomeMessage.moreInfo',
		defaultMessage: 'More Info',
		description: "The word 'more info' displayed when there is no heading or body",
	},
	announcements: {
		id: 'home.welcomeMessage.announcements',
		defaultMessage: 'Announcements',
		description:
			'The text is shown as a heading above the announcements section in the Welcome Message panel on the Home page right sidebar, introducing a list of announcement messages.',
	},
});

const getTextNodes = (nodes: NodeList) =>
	Array.from(nodes).filter((node) => {
		const textContent = node.textContent || '';
		return textContent.trim().length > 0;
	});

const messageIsEmpty = (messageHtml) => {
	if (!messageHtml || typeof window === 'undefined') return true;

	const domParser = new DOMParser();
	const dom = domParser.parseFromString(messageHtml, 'text/html');
	const allTags = dom.querySelectorAll('*');

	return getTextNodes(allTags).length <= 0;
};

const WELCOME_MESSAGE_SECTION_ID = 'home-welcome-message';

type HomeWelcomeMessageProps = {
	onLoad?: () => void;
	noCollapse?: boolean;
};

export const HomeWelcomeMessage: FC<HomeWelcomeMessageProps> = memo(
	({ onLoad, noCollapse = false }) => {
		const [isMessageHovered, setIsMessageHovered] = useState(false);
		const { data, error, loading } = useQueryInterop<
			WelcomeMessagesQuery$data,
			WelcomeMessagesQuery$variables
		>(WelcomeMessagesQuery);
		const messageVals: WelcomeMessagesValue = transformData(data);
		const {
			welcomeMessageHtml,
			welcomeMessageEditUrl,
			showEditButton,
			showWelcomeMessageEditHint,
		} = messageVals;

		const { createAnalyticsEvent } = useAnalyticsEvents();
		const firedRenderedEventRef = useRef(false);

		useEffect(() => {
			if (!loading && !firedRenderedEventRef.current) {
				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						action: 'rendered',
						actionSubject: 'collapsibleSectionContent',
						actionSubjectId: WELCOME_MESSAGE_SECTION_ID,
						source: 'rightPanel',
						attributes: {
							homeVersion: '1.5',
						},
					},
				}).fire();
				firedRenderedEventRef.current = true;
			}
		}, [loading, createAnalyticsEvent]);

		if (loading) return null;

		const CollapsibleSectionComponent = noCollapse ? Fragment : CollapsibleSection;

		if (error) {
			return (
				<CollapsibleSectionComponent
					sectionId={WELCOME_MESSAGE_SECTION_ID}
					header={<FormattedMessage {...i18n.announcements} />}
				>
					<WelcomeMessageError />
					{onLoad && <LoadingDone onLoad={onLoad} />}
				</CollapsibleSectionComponent>
			);
		}

		if (messageIsEmpty(welcomeMessageHtml)) {
			return <ExperienceSuccess name={HOME_RIGHT_PANEL_WELCOME_MESSAGE_EXPERIENCE} />;
		}

		const onMouseEnter = () => {
			setIsMessageHovered(true);
		};

		const onMouseLeave = () => {
			setIsMessageHovered(false);
		};

		const preventEditPropagation = (e) => {
			e.stopPropagation();
		};

		const customizeLinkCompiled = () => (
			<div css={customizeLinkStyles}>
				<Link to={welcomeMessageEditUrl}>
					<FormattedMessage {...i18n.customize} />
				</Link>
			</div>
		);

		const editButtonCompiled = () => (
			<EditLinkCompiled to={welcomeMessageEditUrl} onClick={preventEditPropagation}>
				<Button
					spacing="none"
					iconBefore={<EditIcon label="Edit welcome message" spacing="spacious" />}
				/>
			</EditLinkCompiled>
		);

		return (
			<div
				onMouseEnter={onMouseEnter}
				onMouseLeave={onMouseLeave}
				onFocus={onMouseEnter}
				onBlur={onMouseLeave}
			>
				<CollapsibleSectionComponent
					onInitialCollapse={onLoad}
					sectionId={WELCOME_MESSAGE_SECTION_ID}
					header={
						<div css={headerContainerStyles}>
							<FormattedMessage {...i18n.announcements} />
							{showEditButton && isMessageHovered && editButtonCompiled()}
						</div>
					}
				>
					<section css={welcomeMessageStyles}>
						<div
							css={announcementsContainerStyles}
							// eslint-disable-next-line react/no-danger
							dangerouslySetInnerHTML={{ __html: welcomeMessageHtml || '' }}
						/>
					</section>
					{showWelcomeMessageEditHint && customizeLinkCompiled()}
				</CollapsibleSectionComponent>
				{onLoad && <LoadingDone onLoad={onLoad} />}
			</div>
		);
	},
);
